import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { AdminClient } from "../../../api/generated/APIClient";
import { EntityImageThumbnail } from "../../../components/ui/EntityImageThumbnail";
import { ResultDetailPage } from "./ResultDetailPage";
import { ResultFormPage } from "./ResultFormPage";
import "./ResultPage.css";

// ルートモードの型
type RouteMode = "list" | "detail" | "edit" | "create";

export const ResultPage: React.FC = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [results, setResults] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [currentResult, setCurrentResult] = useState<any | undefined>();

  // ルートからモードを判定
  const getMode = (): RouteMode => {
    const path = location.pathname;
    if (path.includes("/edit")) return "edit";
    if (path.includes("/create")) return "create";
    if (id) return "detail";
    return "list";
  };

  const mode = getMode();

  // Connect-ES APIクライアント呼び出し関数
  const callSystemEntityAPI = async (
    entityName: string,
    operation: "index" | "create" | "update" | "delete",
    data?: any,
    ids?: string[]
  ) => {
    try {
      const entityPascalCase = entityName;
      let methodName: string;
      switch (operation) {
        case "index":
          methodName = `Index${entityPascalCase}`;
          break;
        case "create":
          methodName = `Create${entityPascalCase}`;
          break;
        case "update":
          methodName = `Update${entityPascalCase}`;
          break;
        case "delete":
          methodName = `Delete${entityPascalCase}`;
          break;
        default:
          throw new Error(`未対応の操作: ${operation}`);
      }

      const apiMethod = (AdminClient as any)[methodName];
      if (!apiMethod || typeof apiMethod !== "function") {
        throw new Error(`APIメソッド ${methodName} が見つかりません`);
      }

      let apiArgs: any = {};
      if (operation === "index") {
        apiArgs = {
          offset: 0,
          limit: 0,
        };
      } else if (operation === "delete" && ids) {
        apiArgs = {
          ids: ids,
        };
      } else if (data) {
        const entityArrayKey = entityName.toLowerCase() + "s";
        apiArgs = {
          [entityArrayKey]: [data],
        };
      }

      const result = await apiMethod(apiArgs);
      return result;
    } catch (error) {
      console.error(`${operation} request failed:`, error);
      throw error;
    }
  };

  // データを取得
  useEffect(() => {
    const fetchData = async () => {
      try {
        // 施工実績を取得
        const resultResponse = await callSystemEntityAPI("Result", "index");
        let resultItems: any[] = [];
        if (resultResponse && resultResponse.data) {
          if (Array.isArray(resultResponse.data)) {
            resultItems = resultResponse.data;
          } else if (typeof resultResponse.data === "object") {
            const dataObj = resultResponse.data as any;
            if (dataObj.results && Array.isArray(dataObj.results)) {
              resultItems = dataObj.results;
            } else if (dataObj.items && Array.isArray(dataObj.items)) {
              resultItems = dataObj.items;
            } else if (dataObj.results === null || dataObj.items === null) {
              resultItems = [];
            } else if (Object.keys(dataObj).length === 0) {
              resultItems = [];
            } else if (dataObj.id) {
              resultItems = [dataObj];
            } else {
              resultItems = [];
            }
          }
        }
        setResults(resultItems);

        // カテゴリを取得
        const categoryResponse = await callSystemEntityAPI(
          "ResultCategory",
          "index"
        );
        let categoryItems: any[] = [];
        if (categoryResponse && categoryResponse.data) {
          if (Array.isArray(categoryResponse.data)) {
            categoryItems = categoryResponse.data;
          } else if (typeof categoryResponse.data === "object") {
            const dataObj = categoryResponse.data as any;
            if (
              dataObj.resultCategorys &&
              Array.isArray(dataObj.resultCategorys)
            ) {
              categoryItems = dataObj.resultCategorys;
            } else if (
              dataObj.result_categorys &&
              Array.isArray(dataObj.result_categorys)
            ) {
              categoryItems = dataObj.result_categorys;
            } else if (dataObj.items && Array.isArray(dataObj.items)) {
              categoryItems = dataObj.items;
            }
          }
        }
        setCategories(categoryItems);
      } catch (error) {
        console.error("データ取得エラー:", error);
        setResults([]);
        setCategories([]);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (id) {
      const item = results.find((item) => item.id === id);
      setCurrentResult(item);
    } else {
      setCurrentResult(undefined);
    }
  }, [id, results]);

  const handleEdit = (itemId: string) => {
    navigate(`/admin/entities/result/${itemId}/edit`);
  };

  const handleDelete = async (itemId: string) => {
    if (!confirm("施工実績を削除しますか？")) {
      return;
    }

    try {
      await callSystemEntityAPI("Result", "delete", undefined, [itemId]);
      setResults(results.filter((item) => item.id !== itemId));
      if (id === itemId) {
        navigate(`/admin/entities/result`);
      }
    } catch (error) {
      console.error("削除エラー:", error);
      alert(
        `削除に失敗しました: ${
          error instanceof Error ? error.message : "不明なエラー"
        }`
      );
    }
  };

  const handleCreate = () => {
    navigate(`/admin/entities/result/create`);
  };

  const handleBack = () => {
    navigate(`/admin/entities/result`);
  };

  const handleDetail = (itemId: string) => {
    navigate(`/admin/entities/result/${itemId}`);
  };

  // カテゴリ名を取得
  const getCategoryName = (categoryId: string): string => {
    const category = categories.find((c) => c.id === categoryId);
    return category?.name || "未分類";
  };

  // 作成・編集モードの場合は専用フォームページを表示
  if (mode === "create" || mode === "edit") {
    return <ResultFormPage categories={categories} />;
  }

  // 一覧モードの場合
  if (mode === "list") {
    return (
      <div className="result-list-page">
        <div className="result-list-header">
          <h1>施工実績一覧</h1>
          <div className="result-list-actions">
            <button className="btn btn-primary" onClick={handleCreate}>
              新規作成
            </button>
          </div>
        </div>

        <div className="result-list-content">
          {results.length === 0 ? (
            <div className="result-empty-state">
              <p>施工実績がありません</p>
              <button className="btn btn-primary" onClick={handleCreate}>
                新規作成
              </button>
            </div>
          ) : (
            <div className="result-list-table-container">
              <table className="result-list-table">
                <thead>
                  <tr>
                    <th className="col-image">画像</th>
                    <th className="col-title">タイトル</th>
                    <th className="col-category">カテゴリ</th>
                    <th className="col-status">状態</th>
                    <th className="col-actions">操作</th>
                  </tr>
                </thead>
                <tbody>
                  {results.map((result) => (
                    <tr key={result.id}>
                      <td className="col-image">
                        <EntityImageThumbnail
                          storagePath={
                            result.mainImage?.storagePath ||
                            result.mainImage?.storage_path ||
                            result.main_image?.storagePath ||
                            result.main_image?.storage_path
                          }
                          alt={result.title}
                          size={64}
                          onClick={() => handleDetail(result.id)}
                          className="result-thumbnail"
                        />
                      </td>
                      <td className="col-title">
                        <Link
                          to={`/admin/entities/result/${result.id}`}
                          className="result-title-link"
                        >
                          {result.title}
                        </Link>
                      </td>
                      <td className="col-category">
                        {getCategoryName(
                          result.categoryId || result.category_id
                        )}
                      </td>
                      <td className="col-status">
                        <span
                          className={`status-badge ${
                            result.isPublished ?? result.is_published
                              ? "published"
                              : "draft"
                          }`}
                        >
                          {result.isPublished ?? result.is_published
                            ? "公開"
                            : "非公開"}
                        </span>
                      </td>
                      <td className="col-actions">
                        <div className="action-buttons">
                          <button
                            className="btn btn-sm btn-primary"
                            onClick={() => handleDetail(result.id)}
                          >
                            詳細
                          </button>
                          <button
                            className="btn btn-sm btn-secondary"
                            onClick={() => handleEdit(result.id)}
                          >
                            編集
                          </button>
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() => handleDelete(result.id)}
                          >
                            削除
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    );
  }

  // 詳細モードの場合
  return (
    <ResultDetailPage
      result={currentResult}
      categories={categories}
      onEdit={handleEdit}
      onDelete={handleDelete}
      onBack={handleBack}
    />
  );
};
